/**
 * How numbers read on screen. One place, so "0.84" and "$2.72" look the same on
 * every page, and a missing value is always an em dash rather than "NaN" or "0".
 */

const DASH = "—";

type Maybe<T> = T | null | undefined;

/** A 0–1 score: 0.842 → "0.84". */
export function formatScore(value: Maybe<number>): string {
  return value === null || value === undefined || Number.isNaN(value) ? DASH : value.toFixed(2);
}

/** A 0–1 rate: 0.78 → "78%". */
export function formatPercent(value: Maybe<number>): string {
  return value === null || value === undefined ? DASH : `${Math.round(value * 100)}%`;
}

/** US dollars. Sub-cent amounts keep a third decimal, because judge calls are
 *  routinely $0.004 and "$0.00" would read as free. */
export function formatMoney(usd: Maybe<number>): string {
  if (usd === null || usd === undefined) return DASH;
  const digits = usd !== 0 && Math.abs(usd) < 0.01 ? 3 : 2;
  return `$${usd.toFixed(digits)}`;
}

/** Milliseconds: 4100 → "4.1 s", 850 → "850 ms". */
export function formatLatency(ms: Maybe<number>): string {
  if (ms === null || ms === undefined) return DASH;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function formatCount(n: Maybe<number>): string {
  return n === null || n === undefined ? DASH : n.toLocaleString("en-US");
}

/** "1 item", "3 items". */
export function plural(n: number, word: string, many = `${word}s`): string {
  return `${formatCount(n)} ${n === 1 ? word : many}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "27 Sep", or "27 Sep 2025" outside the current year. */
export function formatDate(iso: Maybe<string>, now: Date = new Date()): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  const day = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? day : `${day} ${d.getFullYear()}`;
}

/** "27 Sep, 10:42". */
export function formatDateTime(iso: Maybe<string>, now: Date = new Date()): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${formatDate(iso, now)}, ${time}`;
}

/** "just now", "5 min ago", "2 h ago", "yesterday", then a date. */
export function formatRelative(iso: Maybe<string>, now: Date = new Date()): string {
  if (!iso) return DASH;
  const seconds = (now.getTime() - new Date(iso).getTime()) / 1000;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 172_800) return "yesterday";
  return formatDate(iso, now);
}

/** Pretty JSON for panels and config views; strings stay as they are. */
export function formatJson(value: unknown): string {
  if (value === null || value === undefined) return DASH;
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

/** One line for a table cell: strings as-is, anything else as compact JSON. */
export function preview(value: unknown, max = 160): string {
  if (value === null || value === undefined || value === "") return DASH;
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Display legacy input variables alongside the remaining input fields. */
export function inputEntries(input: Record<string, unknown>): [string, unknown][] {
  const { variables, ...rest } = input;
  return variables && typeof variables === "object" && !Array.isArray(variables)
    ? [...Object.entries(variables), ...Object.entries(rest)]
    : Object.entries(input);
}
